import React, { useCallback, useEffect, useRef } from "react";
import { bool, func, shape, string } from "prop-types";
import Limits from "pages/v2/transfer/Limits";
import AgendaDrawer from "pages/v2/transfer/AgendaDrawer";
import CreateTemplateDrawerV3 from "v3/pages/transfers/CreateTemplateDrawerV3";
import SchedulerDrawerV3 from "v3/pages/transfers/scheduler/SchedulerDrawerV3";
import TADWarningModal from "pages/v2/transfer/TADWarningModal";
import TemplateDrawerV3 from "v3/pages/transfers/TemplateDrawerV3";
import { transfersTestIds } from "@ui-kit/components/index.es";

/**
 * Sync hook-driven values into Formik when legacy state updates outside submit flow.
 */
export function useTransferExecuteFormikSync(formikProps, fields) {
    const prevFieldsRef = useRef(fields);

    useEffect(() => {
        if (!formikProps?.setFieldValue) {
            return;
        }

        const prevFields = prevFieldsRef.current;
        Object.keys(fields).forEach((key) => {
            if (prevFields[key] !== fields[key]) {
                formikProps.setFieldValue(key, fields[key], false);
            }
        });
        prevFieldsRef.current = fields;
    }, [formikProps, fields]);
}

export function useTransferExecuteLimitsRenderer(logic) {
    return useCallback(() => {
        const account = typeof logic.selectedAccount === "function" ? logic.selectedAccount() : logic.selectedAccount;
        const currency = account?.currency;
        return (
            <section data-testid={transfersTestIds.execute.limits}>
                <Limits
                    values={logic.values}
                    limit={logic.limit}
                    limits={logic.limits}
                    limitUsed={logic.limitUsed}
                    isDesktop={logic.isDesktop}
                    currency={currency}
                />
            </section>
        );
    }, [logic]);
}

export function TransferExecuteModals({ logic, idActivity, idForm, valuesForTemplate, showAgendaDrawer = false }) {
    const isTadModalVisible = Boolean(logic.tadWarningPopupVisible && !logic.fetchingSubmit);

    return (
        <>
            <section data-testid={transfersTestIds.execute.tadModal}>
                <TADWarningModal isVisible={isTadModalVisible} closeModal={logic.closeTADModal} />
            </section>
            {showAgendaDrawer ? (
                <section data-testid={transfersTestIds.execute.agendaDrawer}>
                    <AgendaDrawer />
                </section>
            ) : null}
            <SchedulerDrawerV3
                isVisible={logic.isScheduleModalVisible}
                onClose={logic.closeScheduleModal}
                scheduler={logic.values?.scheduler}
                onScheduleClick={logic.handleSchedulerChange}
            />
            <CreateTemplateDrawerV3
                values={valuesForTemplate ?? logic.values}
                idActivityTemplate={idActivity}
                idForm={idForm}
            />
            <TemplateDrawerV3
                isVisible={logic.isTemplatesVisible}
                setVisible={logic.setIsTemplatesVisible}
                templates={logic.templates}
                onSelect={logic.handleTemplateSelect}
            />
        </>
    );
}

TransferExecuteModals.propTypes = {
    logic: shape({
        tadWarningPopupVisible: bool,
        fetchingSubmit: bool,
        closeTADModal: func,
        isScheduleModalVisible: bool,
        closeScheduleModal: func,
        values: shape({ scheduler: shape({}) }),
        handleSchedulerChange: func,
        isTemplatesVisible: bool,
        setIsTemplatesVisible: func,
        templates: shape({}),
        handleTemplateSelect: func,
    }).isRequired,
    idActivity: string.isRequired,
    idForm: string.isRequired,
    valuesForTemplate: shape({}),
    showAgendaDrawer: bool,
};

TransferExecuteModals.defaultProps = {
    valuesForTemplate: null,
    showAgendaDrawer: false,
};

/** Hooks expose `selectedCurrency` as getter function (V2 parity). */
export function resolveTransferExecuteCurrency(selectedCurrency) {
    if (typeof selectedCurrency === "function") {
        return selectedCurrency();
    }
    return selectedCurrency ?? "";
}
